import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Award,
  Filter,
  Gamepad2,
  Loader2,
  Medal,
  Megaphone,
  Puzzle,
  ShoppingBag,
  Tag,
  Trophy,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, LabelList } from 'recharts';
import api from '../api/client';

// Real-data activity tables for the Activity page, below the Feature Usage
// Heatmap. One café filter (shared across every tab) plus a table per
// activity type, each backed by its own /internal/activity/* endpoint.

const POLL_MS = 60000;

const TABS = [
  { key: 'games', label: 'Games', icon: Gamepad2 },
  { key: 'apps', label: 'Apps', icon: Puzzle },
  { key: 'leaderboard', label: 'Leaderboard', icon: Trophy },
  { key: 'shop', label: 'Shop', icon: ShoppingBag },
  { key: 'coupons', label: 'Coupons', icon: Tag },
  { key: 'campaigns', label: 'Campaigns', icon: Megaphone },
  { key: 'tournaments', label: 'Tournaments', icon: Award },
  { key: 'badges', label: 'Badges', icon: Medal },
];

const ENDPOINT = {
  games: '/internal/activity/games',
  apps: '/internal/activity/apps',
  leaderboard: '/internal/activity/leaderboard',
  shop: '/internal/activity/shop',
  coupons: '/internal/activity/coupons',
  campaigns: '/internal/activity/campaigns',
  tournaments: '/internal/activity/tournaments',
  badges: '/internal/activity/badges',
};

// "1hr 40 mins" — same convention the café admin console's own Activity page
// already uses (primus-admin-main/src/utils/duration.js), so the number here
// reads the same way staff already expect.
function formatMinutes(value) {
  const total = Math.floor(Number(value));
  if (!Number.isFinite(total) || total < 0) return '—';
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  const hourPart = hours > 0 ? `${hours}${hours === 1 ? 'hr' : 'hrs'}` : '';
  const minPart = mins > 0 ? `${mins}${mins === 1 ? ' min' : ' mins'}` : '';
  if (hourPart && minPart) return `${hourPart} ${minPart}`;
  return hourPart || minPart || '0 mins';
}

function formatAgo(iso) {
  if (!iso) return '—';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function formatDateTime(iso) {
  if (!iso) return undefined;
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
  });
}

// Games/Apps get a "most played" bar chart above their table -- rows are
// already one-per-title, sorted by minutes desc (see internal_dashboard.py),
// so this is just the top slice, no re-sorting needed.
const CHART_TOP_N = 8;
const CHART_NAME_KEY = { games: 'game', apps: 'app' };

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="at-chart-tip">
      <div className="at-chart-tip__name">{p.name}</div>
      <div className="at-chart-tip__row">{formatMinutes(p.minutes)}</div>
      <div className="at-chart-tip__row">{p.players} player{p.players === 1 ? '' : 's'} · {p.launches} launch{p.launches === 1 ? '' : 'es'}</div>
    </div>
  );
}

const STATUS_TONE = {
  redeemed: 'good', completed: 'good', active: 'good',
  used: 'neutral', registered: 'neutral', expired: 'neutral',
  cancelled: 'bad', refunded: 'bad', dropped: 'bad',
};

function StatusPill({ value }) {
  if (!value) return <span className="at-muted">—</span>;
  const tone = STATUS_TONE[value] || 'neutral';
  return <span className={`at-status at-status--${tone}`}>{value}</span>;
}

// Games/Apps only: whether a row's numbers came from real process + focus-window
// measurement (see backend's _compute_activity_launches) or the old launch-gap
// guess. Never blended silently -- a row is "Measured" only when EVERY minute
// counted for it this period was real, so a mixed row still reads as "Estimated".
function MeasuredPill({ measured }) {
  return (
    <span
      className={`at-status at-status--${measured ? 'good' : 'neutral'}`}
      title={measured
        ? 'Measured from real process lifetime + foreground-window time'
        : 'Estimated from launch-order timing (no direct measurement reported for this period)'}
    >
      {measured ? 'Measured' : 'Estimated'}
    </span>
  );
}

// Players tab: who played which game/app. A funnel icon opens a title search; a
// search shows only that title's players ("Top 10 players of X") and scrolling
// the list loads the next ones. Paging is server-side (offset/limit).
const PLAYER_PAGE = 10;

function PlayerGameTime({ cafeId, cafeLabel }) {
  const [data, setData] = useState(null); // { key, rows, total, hasMore, ratio }
  const [failedKey, setFailedKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [game, setGame] = useState('');
  const [category, setCategory] = useState('');
  const [retry, setRetry] = useState(0);
  const scroller = useRef(null);

  // Every café / search / filter change is a new key and restarts at the top 10;
  // data from an older key is simply never shown.
  const reqKey = `${cafeId ?? 'all'}|${game}|${category}|${retry}`;
  const current = data && data.key === reqKey ? data : null;
  const rows = current ? current.rows : null;
  const hasMore = current ? current.hasMore : false;
  const error = failedKey === reqKey;

  const fetchPage = useCallback(async (offset) => {
    const params = { offset, limit: PLAYER_PAGE };
    if (cafeId != null) params.cafe_id = cafeId;
    if (game) params.game = game;
    if (category) params.category = category;
    const res = await api.get('/internal/activity/game-players', { params });
    return res.data;
  }, [cafeId, game, category]);

  useEffect(() => {
    let alive = true;
    fetchPage(0)
      .then((d) => {
        if (!alive) return;
        setData({
          key: reqKey, rows: d.rows || [], total: d.total || 0, hasMore: !!d.has_more, ratio: d.measured_ratio,
        });
      })
      .catch(() => { if (alive) setFailedKey(reqKey); });
    return () => { alive = false; };
  }, [fetchPage, reqKey]);

  const loadMore = useCallback(async () => {
    if (busy || !hasMore || !rows) return;
    setBusy(true);
    try {
      const d = await fetchPage(rows.length);
      setData((cur) => (cur && cur.key === reqKey
        ? { ...cur, rows: [...cur.rows, ...(d.rows || [])], hasMore: !!d.has_more }
        : cur));
    } catch {
      // keep what is shown; stop paging until a new search or café
      setData((cur) => (cur && cur.key === reqKey ? { ...cur, hasMore: false } : cur));
    } finally {
      setBusy(false);
    }
  }, [busy, hasMore, rows, fetchPage, reqKey]);

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) loadMore();
  };

  // If the first page does not fill the box there is nothing to scroll: fetch on.
  useEffect(() => {
    const el = scroller.current;
    if (el && hasMore && !busy && el.scrollHeight <= el.clientHeight + 1) loadMore();
  }, [rows, hasMore, busy, loadMore]);

  const submit = (e) => {
    e.preventDefault();
    setGame(draft.trim());
  };
  const clear = () => { setDraft(''); setGame(''); };

  const heading = game ? `Top ${PLAYER_PAGE} players of ${game}` : 'Top players by time played';
  const head = game
    ? ['#', 'Player', 'Café', 'Time Played', 'Launches', 'Last Played']
    : ['#', 'Player', 'Game / App', 'Café', 'Time Played', 'Launches', 'Last Played'];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '4px 0 8px' }}>
        <h4 className="at__chart-title" style={{ margin: 0 }}>{heading}</h4>
        {game && (
          <button type="button" className="btn btn--secondary btn--sm" onClick={clear}>Clear</button>
        )}
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          aria-label="Filter by game or app"
          aria-expanded={open}
          title="Search a game or app"
          onClick={() => setOpen((v) => !v)}
        >
          <Filter size={13} aria-hidden="true" />
        </button>
        {['', 'game', 'app'].map((c) => (
          <button
            key={c || 'all'}
            type="button"
            className={`at__tab ${category === c ? 'at__tab--active' : ''}`}
            onClick={() => setCategory(c)}
          >
            {c === '' ? 'All' : c === 'game' ? 'Games' : 'Apps'}
          </button>
        ))}
      </div>
      {open && (
        <form onSubmit={submit} style={{ display: 'flex', gap: 6, margin: '0 0 8px' }}>
          <input
            className="at__select"
            style={{ width: '13rem', maxWidth: '100%' }}
            placeholder="Game or app name"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <button type="submit" className="btn btn--secondary btn--sm" style={{ padding: '3px 10px', fontSize: 12 }}>
            Search
          </button>
        </form>
      )}
      <div className="at__scroll" ref={scroller} onScroll={onScroll} style={{ maxHeight: '22rem', overflowY: 'auto' }}>
        {!rows && !error && (
          <div className="at__state">
            <Loader2 size={15} className="at-spin" aria-hidden="true" />
            Loading {cafeLabel.toLowerCase()}'s players…
          </div>
        )}
        {error && !rows && (
          <div className="at__state at__state--error">
            Couldn't load players.
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => setRetry((n) => n + 1)}>
              Try again
            </button>
          </div>
        )}
        {rows && rows.length === 0 && (
          <div className="at__empty">{game ? `No players found for "${game}".` : 'No play time recorded yet.'}</div>
        )}
        {rows && rows.length > 0 && (
          <table className="at__table">
            <thead>
              <tr>{head.map((h) => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.user_id}|${r.game}|${r.category}`}>
                  <td><span className={`at-rank ${i < 3 ? `at-rank--${i + 1}` : ''}`}>{i + 1}</span></td>
                  <td><span className="at-name">{r.name}</span></td>
                  {!game && <td>{r.game}</td>}
                  <td>{r.cafe_name}</td>
                  <td>
                    <span
                      className="at-num"
                      title={r.measured ? 'Measured by the kiosk' : 'Estimated from launch gaps'}
                    >
                      {formatMinutes(r.minutes)}
                      {!r.measured && <span className="at-muted" style={{ marginLeft: 4, fontSize: 11 }}>est.</span>}
                    </span>
                  </td>
                  <td><span className="at-num">{r.launches}</span></td>
                  <td><span title={formatDateTime(r.last_played)}>{formatAgo(r.last_played)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {busy && (
          <div className="at__state"><Loader2 size={15} className="at-spin" aria-hidden="true" />Loading more…</div>
        )}
      </div>
    </div>
  );
}

const COLUMNS = {
  games: {
    head: ['Game', 'Café', 'Players', 'Time Played', 'Running', 'Accuracy', 'Launches', 'Last Played'],
    empty: 'No game launches recorded yet.',
    row: (r) => [
      <span className="at-name">{r.game}</span>,
      r.cafe_name,
      <span className="at-num">{r.players}</span>,
      <span className="at-num">{formatMinutes(r.minutes)}</span>,
      <span className="at-num at-muted">{formatMinutes(r.running_minutes)}</span>,
      <MeasuredPill measured={r.measured} />,
      <span className="at-num">{r.launches}</span>,
      <span title={formatDateTime(r.last_played)}>{formatAgo(r.last_played)}</span>,
    ],
  },
  apps: {
    head: ['App', 'Café', 'Players', 'Time Open', 'Running', 'Accuracy', 'Launches', 'Last Used'],
    empty: 'No app launches recorded yet.',
    row: (r) => [
      <span className="at-name">{r.app}</span>,
      r.cafe_name,
      <span className="at-num">{r.players}</span>,
      <span className="at-num">{formatMinutes(r.minutes)}</span>,
      <span className="at-num at-muted">{formatMinutes(r.running_minutes)}</span>,
      <MeasuredPill measured={r.measured} />,
      <span className="at-num">{r.launches}</span>,
      <span title={formatDateTime(r.last_used)}>{formatAgo(r.last_used)}</span>,
    ],
  },
  leaderboard: {
    head: ['Rank', 'Customer', 'Café', 'Time Played'],
    empty: 'No play time recorded yet.',
    row: (r) => [
      <span className={`at-rank ${r.rank <= 3 ? `at-rank--${r.rank}` : ''}`}>{r.rank}</span>,
      <span className="at-name">{r.user_name}</span>,
      r.cafe_name || 'Multiple cafés',
      <span className="at-num">{formatMinutes(r.minutes)}</span>,
    ],
  },
  shop: {
    head: ['Item', 'Café', 'Orders', 'Qty Sold', 'Revenue', 'Last Ordered'],
    empty: 'No purchases recorded yet.',
    row: (r) => [
      <span className="at-name">{r.item}</span>,
      r.cafe_name,
      <span className="at-num">{r.orders}</span>,
      <span className="at-num">{r.qty}</span>,
      <span className="at-num">₹{Number(r.revenue).toLocaleString('en-IN')}</span>,
      <span title={formatDateTime(r.last_ordered)}>{formatAgo(r.last_ordered)}</span>,
    ],
  },
  coupons: {
    head: ['Customer', 'Café', 'Coupon', 'Discount', 'Status', 'Date'],
    empty: 'No coupon activity recorded yet.',
    row: (r) => [
      <span className="at-name">{r.user_name}</span>,
      r.cafe_name,
      <code className="at-code">{r.code}</code>,
      r.discount,
      <StatusPill value={r.status} />,
      <span title={formatDateTime(r.date)}>{formatAgo(r.date)}</span>,
    ],
  },
  campaigns: {
    head: ['Customer', 'Café', 'Campaign', 'Reward', 'Status', 'Date'],
    empty: 'No campaign activity recorded yet.',
    row: (r) => [
      <span className="at-name">{r.user_name}</span>,
      r.cafe_name,
      r.campaign_name,
      r.reward,
      <StatusPill value={r.status} />,
      <span title={formatDateTime(r.date)}>{formatAgo(r.date)}</span>,
    ],
  },
  tournaments: {
    head: ['Customer', 'Café', 'Tournament', 'Result', 'Prize', 'Date'],
    empty: 'No tournament activity recorded yet.',
    row: (r) => [
      <span className="at-name">{r.user_name}</span>,
      r.cafe_name,
      r.tournament_name,
      /^#\d+$/.test(r.result)
        ? <span className="at-status at-status--good">{r.result}</span>
        : <StatusPill value={r.result} />,
      r.prize || <span className="at-muted">—</span>,
      <span title={formatDateTime(r.date)}>{formatAgo(r.date)}</span>,
    ],
  },
  badges: {
    head: ['Customer', 'Café', 'Badge', 'Tier', 'Earned'],
    empty: 'No badges earned yet.',
    row: (r) => [
      <span className="at-name">{r.user_name}</span>,
      r.cafe_name,
      <span className="at-badge" title={r.description || undefined}>
        {r.icon && <span aria-hidden="true">{r.icon}</span>}
        {r.badge_name}
      </span>,
      r.tier
        ? <span className={`at-tier at-tier--${r.tier}`}>{r.tier}</span>
        : <span className="at-muted">—</span>,
      <span title={formatDateTime(r.date)}>{formatAgo(r.date)}</span>,
    ],
  },
};

// Its own card, shown under Customer Activity on the Activity page: who played
// the most, with a game/app search and "load more" on scroll.
export function PlayerGameTimeCard() {
  const [cafes, setCafes] = useState([]);
  const [cafeId, setCafeId] = useState(null); // null = All Cafés

  useEffect(() => {
    let alive = true;
    api.get('/internal/cafes')
      .then((res) => { if (alive) setCafes(res.data || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const cafeLabel = cafeId == null
    ? 'All Cafés'
    : cafes.find((c) => c.id === cafeId)?.name || `Café #${cafeId}`;

  return (
    <div className="at card">
      <div className="at__header">
        <div>
          <h3 className="at__title">Player Game Time</h3>
          <p className="at__subtitle">Who played the most — search a game or app to see its top players</p>
        </div>
        <div className="at__filter">
          <label htmlFor="pgt-cafe-select" className="at__filter-label">Café</label>
          <select
            id="pgt-cafe-select"
            className="at__select"
            value={cafeId == null ? 'all' : String(cafeId)}
            onChange={(e) => setCafeId(e.target.value === 'all' ? null : Number(e.target.value))}
          >
            <option value="all">All Cafés</option>
            {cafes.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>
      <PlayerGameTime cafeId={cafeId} cafeLabel={cafeLabel} />
      <style>{STYLES}</style>
    </div>
  );
}

export default function ActivityTables() {
  const [cafes, setCafes] = useState([]);
  const [cafeId, setCafeId] = useState(null); // null = All Cafés
  const [tab, setTab] = useState('games');
  const [state, setState] = useState({}); // key -> { rows, loading, error }

  useEffect(() => {
    let alive = true;
    api.get('/internal/cafes')
      .then((res) => { if (alive) setCafes(res.data || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const load = useCallback(async (key, cid) => {
    try {
      const params = {};
      if (cid != null) params.cafe_id = cid;
      const res = await api.get(ENDPOINT[key], { params });
      // measured_ratio only exists on the games/apps responses (see
      // internal_dashboard.ActivityGamesResponse/ActivityAppsResponse) --
      // undefined elsewhere, which the display below already guards on.
      setState((s) => ({
        ...s,
        [key]: { rows: res.data?.rows || [], measuredRatio: res.data?.measured_ratio, error: false },
      }));
    } catch {
      setState((s) => ({ ...s, [key]: { rows: (s[key] || {}).rows, error: true } }));
    }
  }, []);

  useEffect(() => {
    let alive = true;
    const run = () => {
      if (alive) load(tab, cafeId);
    };
    run();
    const id = setInterval(run, POLL_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [tab, cafeId, load]);

  const entry = state[tab];
  const rows = entry?.rows;
  const cols = COLUMNS[tab];
  const chartNameKey = CHART_NAME_KEY[tab];

  const chartData = useMemo(() => {
    if (!chartNameKey || !rows?.length) return [];
    return rows.slice(0, CHART_TOP_N).map((r) => ({
      name: r[chartNameKey], minutes: r.minutes, players: r.players, launches: r.launches,
    }));
  }, [rows, chartNameKey]);

  const cafeLabel = useMemo(() => {
    if (cafeId == null) return 'All Cafés';
    return cafes.find((c) => c.id === cafeId)?.name || `Café #${cafeId}`;
  }, [cafeId, cafes]);

  return (
    <div className="at card">
      <div className="at__header">
        <div>
          <h3 className="at__title">Customer Activity</h3>
          <p className="at__subtitle">Real games, apps, leaderboard, shop, coupon, campaign, tournament and badge activity</p>
        </div>
        <div className="at__filter">
          <label htmlFor="at-cafe-select" className="at__filter-label">Café</label>
          <select
            id="at-cafe-select"
            className="at__select"
            value={cafeId == null ? 'all' : String(cafeId)}
            onChange={(e) => setCafeId(e.target.value === 'all' ? null : Number(e.target.value))}
          >
            <option value="all">All Cafés</option>
            {cafes.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="at__tabs" role="tablist" aria-label="Activity type">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`at__tab ${tab === t.key ? 'at__tab--active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              <Icon size={14} aria-hidden="true" />
              {t.label}
            </button>
          );
        })}
      </div>

      {(tab === 'games' || tab === 'apps') && typeof entry?.measuredRatio === 'number' && (
        // Production health signal (accuracy-tracking brief §7): a drop at one café
        // means tracking broke there -- kiosks crashing, a launcher autostarting
        // outside the session job, or a catalog path gone stale.
        <div
          className="at__measured-ratio"
          title="Share of this period's Time Played/Open figures that came from real process + focus-window measurement, not the launch-order estimate"
        >
          {Math.round(entry.measuredRatio * 100)}% of playtime measured this period
        </div>
      )}

      {chartData.length > 0 && (
        <div className="at__chart">
          <h4 className="at__chart-title">
            Most played {tab === 'games' ? 'games' : 'apps'} · by time {tab === 'games' ? 'played' : 'open'}
          </h4>
          <ResponsiveContainer width="100%" height={Math.max(140, chartData.length * 34)}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 48, bottom: 4, left: 4 }}>
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="name"
                width={130}
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 12 }}
              />
              <RechartsTooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
              <Bar dataKey="minutes" fill="#FE7B00" radius={[0, 4, 4, 0]} maxBarSize={22}>
                <LabelList
                  dataKey="minutes"
                  position="right"
                  formatter={formatMinutes}
                  style={{ fill: 'var(--text-secondary, #aeb4c0)', fontSize: 11, fontWeight: 600 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="at__scroll">
        {!rows && !entry?.error && (
          <div className="at__state">
            <Loader2 size={15} className="at-spin" aria-hidden="true" />
            Loading {cafeLabel.toLowerCase()}'s activity…
          </div>
        )}
        {entry?.error && !rows && (
          <div className="at__state at__state--error">
            Couldn't load this table.
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => load(tab, cafeId)}>
              Try again
            </button>
          </div>
        )}
        {rows && rows.length === 0 && (
          <div className="at__empty">{cols.empty}</div>
        )}
        {rows && rows.length > 0 && (
          <table className="at__table">
            <thead>
              <tr>{cols.head.map((h) => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                // Games/Apps/Shop are one row per title (unique already); the
                // remaining tabs are still per-customer, per-event rows.
                const rowKey = r.game || r.app || r.item
                  || `${r.user_name}|${r.campaign_name || r.tournament_name || r.badge_code || r.code || ''}|${r.date || ''}`;
                return (
                  <tr key={rowKey}>
                    {cols.row(r).map((cell, j) => <td key={`${rowKey}:${j}`}>{cell}</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <style>{STYLES}</style>
    </div>
  );
}

const STYLES = `
  .at {
    margin-top: var(--space-6);
    padding: var(--space-6);
  }

  .at__header {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--space-4);
    margin-bottom: var(--space-5);
  }

  .at__title {
    font-size: var(--text-lg);
    font-weight: 600;
    letter-spacing: -0.01em;
  }

  .at__subtitle {
    margin-top: 4px;
    font-size: var(--text-sm);
    color: var(--text-tertiary);
  }

  .at__filter {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .at__filter-label {
    font-size: var(--text-xs);
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--text-tertiary);
  }

  .at__select {
    appearance: none;
    padding: 8px 30px 8px 12px;
    border: 1px solid var(--glass-border);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.04)
      url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="10" height="6"><path d="M1 1l4 4 4-4" stroke="%23a8a8b3" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>')
      no-repeat right 10px center;
    color: var(--text-primary);
    font: inherit;
    font-size: var(--text-sm);
    font-weight: 550;
    min-width: 170px;
    cursor: pointer;
  }

  .at__select:focus-visible {
    outline: 2px solid var(--accent-primary, #3987e5);
    outline-offset: 2px;
  }

  /* The closed control inherits the card's dark backdrop through its own
     translucent background, but the native dropdown popup is a separate
     surface most browsers render opaque white by default -- without an
     explicit background here, the light option text (inherited color)
     was landing on that white popup, unreadable. */
  .at__select option {
    background: var(--bg-elevated, #12121a);
    color: var(--text-primary);
  }

  .at__tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: var(--space-4);
  }

  .at__chart {
    margin-bottom: var(--space-5);
    padding: var(--space-4) var(--space-4) var(--space-2);
    border: 1px solid var(--glass-border);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.02);
  }

  .at__chart-title {
    margin: 0 0 var(--space-3);
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--text-secondary);
  }

  .at-chart-tip {
    padding: 8px 10px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    background: rgba(20, 20, 28, 0.97);
    backdrop-filter: blur(16px);
    font-size: 12px;
    color: var(--text-primary);
  }

  .at-chart-tip__name {
    margin-bottom: 4px;
    font-weight: 650;
  }

  .at-chart-tip__row {
    color: var(--text-tertiary);
  }

  .at__tab {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 8px 14px;
    border: 1px solid var(--glass-border);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.03);
    color: var(--text-secondary);
    font: inherit;
    font-size: var(--text-sm);
    font-weight: 600;
    cursor: pointer;
    transition: background 120ms ease-out, color 120ms ease-out, border-color 120ms ease-out;
  }

  .at__tab:hover {
    border-color: rgba(255, 255, 255, 0.16);
    color: var(--text-primary);
  }

  .at__tab:focus-visible {
    outline: 2px solid var(--accent-primary, #3987e5);
    outline-offset: 2px;
  }

  .at__tab--active {
    background: var(--accent-gradient, linear-gradient(135deg, #3987e5, #256abf));
    border-color: transparent;
    color: #fff;
  }

  .at__scroll {
    overflow-x: auto;
    overflow-y: auto;
    max-height: 420px;
    margin: 0 calc(var(--space-2) * -1);
    padding: 0 var(--space-2);
  }

  .at__table {
    width: 100%;
    min-width: 640px;
    border-collapse: collapse;
    font-size: var(--text-sm);
  }

  /* Sticky needs to be on BOTH thead and th -- th alone silently no-ops
     (Chrome keeps reporting position: sticky on it, but the header still
     scrolls away) since <thead> is what actually anchors to the scrolling
     ancestor here. */
  .at__table thead {
    position: sticky;
    top: 0;
    z-index: 1;
  }

  .at__table thead th {
    position: sticky;
    top: 0;
    text-align: left;
    padding: 10px 14px;
    font-size: 11px;
    font-weight: 650;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--text-tertiary);
    background: var(--bg-elevated, #12121a);
    border-bottom: 1px solid var(--glass-border);
    white-space: nowrap;
  }

  .at__table tbody td {
    padding: 11px 14px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.045);
    color: var(--text-secondary);
    vertical-align: middle;
    white-space: nowrap;
  }

  .at__table tbody tr:last-child td {
    border-bottom: 0;
  }

  .at__table tbody tr:hover {
    background: rgba(255, 255, 255, 0.025);
  }

  .at-name {
    color: var(--text-primary);
    font-weight: 600;
  }

  .at-num {
    font-variant-numeric: tabular-nums;
    color: var(--text-primary);
    font-weight: 600;
  }

  .at-code {
    padding: 2px 6px;
    border-radius: 5px;
    background: rgba(255, 255, 255, 0.06);
    font-size: 12px;
  }

  .at-muted {
    color: var(--text-tertiary);
  }

  .at-rank {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 7px;
    font-size: 12px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--text-secondary);
    background: rgba(255, 255, 255, 0.06);
  }

  .at-rank--1 { background: linear-gradient(135deg, #ffd76a, #f5a623); color: #3a2400; }
  .at-rank--2 { background: linear-gradient(135deg, #dfe4ea, #aeb6c2); color: #22262c; }
  .at-rank--3 { background: linear-gradient(135deg, #e3a765, #c17a3a); color: #301b06; }

  .at-status {
    display: inline-flex;
    align-items: center;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 11.5px;
    font-weight: 650;
    text-transform: capitalize;
  }

  .at-status--good { background: rgba(40, 194, 129, 0.14); color: #7bdcae; }
  .at-status--neutral { background: rgba(255, 255, 255, 0.07); color: var(--text-tertiary); }
  .at-status--bad { background: rgba(247, 48, 47, 0.14); color: #f28b8b; }

  .at-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--text-primary);
    font-weight: 600;
  }

  .at-badge span {
    font-size: 15px;
    line-height: 1;
  }

  .at-tier {
    display: inline-flex;
    align-items: center;
    padding: 2px 9px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
  }

  .at-tier--bronze { background: rgba(193, 122, 58, 0.2); color: #e3a765; }
  .at-tier--silver { background: rgba(174, 182, 194, 0.18); color: #cfd6e0; }
  .at-tier--gold { background: rgba(245, 166, 35, 0.18); color: #ffcc70; }
  .at-tier--platinum { background: rgba(138, 180, 216, 0.18); color: #a8c8e8; }
  .at-tier--diamond { background: rgba(110, 231, 240, 0.16); color: #7ee3ec; }

  .at-spin {
    animation: at-spin 0.9s linear infinite;
    vertical-align: -2px;
  }

  @keyframes at-spin {
    to { transform: rotate(360deg); }
  }

  .at__state {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: var(--space-5);
    font-size: var(--text-sm);
    color: var(--text-secondary);
  }

  .at__state--error {
    color: var(--status-danger);
  }

  .at__empty {
    padding: var(--space-5);
    border: 1px dashed var(--glass-border);
    border-radius: 12px;
    font-size: var(--text-sm);
    color: var(--text-secondary);
  }

  @media (prefers-reduced-motion: reduce) {
    .at-spin { animation: none; }
  }
`;
